import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { convertDateTimeCells } from "../Shared/importMapping";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { sharedTableBodyCellSx, globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  FREQUENCIES,
  IMPORT_ACCEPT,
  IMPORT_FIELDS,
  IMPORT_GUIDELINES,
  IMPORT_MAX_BYTES,
  REPAYMENT_MODES,
  TYPES,
  applyServerChecks,
  autoMapColumns,
  checkImportRow,
  importTemplateRows,
  matchFrequency,
  matchMode,
  matchType,
  missingRequiredFields,
  parseDate,
  splitSheetRows,
  validateImportRows,
} from "./loanImportData";
import { importAdvanceRequests, validateAdvanceImport } from "./loansApi";
import { formatDate, formatMoney } from "../Shared/formatters";
import { WizardStepper } from "../Shared/WizardStepper";
import { DateField, SelectField, TextField, toOptions } from "../Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";

// Payroll > Pay Inputs > Loans & Advances > Import (same wizard as the other
// Pay Inputs imports): Upload File -> Map Columns -> Preview & Import, full
// width. Each row becomes a new advance / loan request (Pending, sent to the
// employee's approver). Rows that pass the browser checks are checked by
// POST advance/request/import/validate/ before the preview, and saved by
// POST advance/request/import/.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const MONEY_FIELDS = new Set(["amount"]);
const DATE_FIELDS = new Set(["request_date", "repayment_date"]);
const MONTH_FIELDS = new Set(["payroll_month"]);
const errorText = "#DC2626";

const formatBytes = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const downloadTemplate = () => {
  const sheet = XLSX.utils.aoa_to_sheet(importTemplateRows());
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Loans & Advances");
  XLSX.writeFile(book, "Loans and Advances Import Template.xlsx");
};

// Same reader as the Shift Earnings import: date cells become "YYYY-MM-DD"
// text from the cell's own day number (cellDates gives the day before in
// IST), and CSV cells stay as typed.
const readSheet = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      try {
        const book = XLSX.read(reader.result, { type: "array", cellNF: true, raw: true });
        const sheet = convertDateTimeCells(book.Sheets[book.SheetNames[0]], XLSX.SSF);
        resolve(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" }));
      } catch (error) {
        reject(new Error("This file could not be read. Use the template."));
      }
    };
    reader.readAsArrayBuffer(file);
  });

const rawText = (value) => (value === null || value === undefined || value === "" ? "–" : String(value));

// ---- Step 1: Upload ----

const NumberBadge = ({ value }) => (
  <Mui.Box
    sx={{
      ...designType.captionText,
      flexShrink: 0,
      width: 20,
      height: 20,
      borderRadius: "50%",
      backgroundColor: designColors.slate100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {value}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: designRadii.iconButton,
        backgroundColor: "#E7F8EF",
        color: "#12B76A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>
        {file.name}
      </Mui.Typography>
      <Mui.Typography sx={designType.captionText}>
        {file.rowCount.toLocaleString("en-IN")} rows · {formatBytes(file.size)}
      </Mui.Typography>
    </Mui.Box>
    {onChange ? (
      <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>
        Change File
      </SecondaryButton>
    ) : null}
  </Mui.Stack>
);

const UploadStep = ({ file, error, onFile }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => {
    const next = files?.[0];
    if (next) onFile(next);
  };
  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2, alignItems: "stretch" }}>
      <Mui.Stack spacing={2} sx={{ minHeight: "100%" }}>
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); pick(event.dataTransfer.files); }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input ref={inputRef} hidden type="file" accept={IMPORT_ACCEPT} onChange={(event) => { pick(event.target.files); event.target.value = ""; }} />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or{" "}
            <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>
              click to browse
            </Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>
            Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB
          </Mui.Typography>
          <Mui.Button
            startIcon={<FileDownloadOutlined />}
            onClick={(event) => { event.stopPropagation(); downloadTemplate(); }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download Loans & Advances Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={2} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner
          title="Template Information"
          body="Use the template to raise loan and advance requests in bulk. Each row is created as a Pending request and sent to the employee's approver, like a single request."
        />
        <Mui.Box sx={{ ...detailCardSx, flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {IMPORT_GUIDELINES.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <NumberBadge value={index + 1} />
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---- Step 2: Map columns ----

const MapStep = ({ file, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingRequiredFields(mapping);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={2}>
      <FileCard file={file} onChange={onChangeFile} />
      <Mui.Box sx={detailCardSx}>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 2 }}>
          <Mui.Box>
            <Mui.Typography sx={designType.sectionHeading}>Map File Columns</Mui.Typography>
            <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>
              Columns were matched automatically by header name and content —
              they can be in any order and renamed. Check each match and change
              it if needed.
            </Mui.Typography>
          </Mui.Box>
          <Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>
            Reset Mapping
          </Mui.Button>
        </Mui.Stack>
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                const field = IMPORT_FIELDS.find((item) => item.key === fieldKey);
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>
                      {header || `Column ${index + 1}`}
                      {field?.required ? <Mui.Box component="span" sx={{ color: "#DC2626", ml: 0.5 }}>*</Mui.Box> : null}
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...IMPORT_FIELDS.filter((option) => !usedFields.has(option.key) || option.key === fieldKey).map((option) => ({
                            value: option.key,
                            label: `${option.label}${option.required ? " *" : ""}`,
                          })),
                        ]}
                        placeholder="Do not import"
                        disablePortal={false}
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

// Valid rows show the values that will be sent (fields that don't apply to
// the row's type are left out, so they show "–"); error rows show the file.
const previewValue = (result, key) => {
  const row = result.row;
  if (!row) return rawText(result.raw[key]);
  const value = row[key];
  if (value === null || value === undefined || value === "") return "–";
  if (MONTH_FIELDS.has(key)) return moment(value, "YYYY-MM-DD").format("MMM YYYY");
  if (DATE_FIELDS.has(key)) return formatDate(value);
  if (MONEY_FIELDS.has(key)) return formatMoney(value);
  return String(value);
};

// Same pieces as the other Pay Inputs imports (Penalties, Leave, Overtime,
// Shift Earnings): tab buttons, paged lists, summary card.
const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

const isBlankText = (value) => value === null || value === undefined || String(value).trim() === "";

// Error rows are tinted red; clicking one opens the Edit panel (RowFixPanel)
// to correct it and check it again.
const errorRowSx = {
  cursor: "pointer",
  backgroundColor: "#FEF2F2",
  "&:hover": { backgroundColor: "#FEE2E2" },
  "&:focus-visible": { outline: `2px solid ${errorText}`, outlineOffset: -2 },
};

const PreviewStep = ({ results, onFixRow }) => {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  const valid = results.filter((result) => result.row);
  const invalid = results.filter((result) => !result.row);
  const counts = { all: results.length, valid: valid.length, error: invalid.length };

  const shown = useMemo(() => {
    const base = tab === "valid" ? valid : tab === "error" ? invalid : results;
    const needle = query.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((result) => Object.values(result.raw).some((value) => rawText(value).toLowerCase().includes(needle)));
  }, [tab, query, results, valid, invalid]);
  const { pageItems: pageRows, footer } = usePaged(shown, `${tab}:${query}`);

  return (
    <Mui.Stack spacing={1.5}>
      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>Summary</Mui.Typography>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          {/* Cards pick the rows shown below, same as the tabs. */}
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
        </Mui.Box>
        {invalid.length ? (
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner
              tone="warning"
              body={`${invalid.length.toLocaleString("en-IN")} row(s) have errors and will not be imported. Click a red row to correct it, or import only the valid rows.`}
            />
          </Mui.Box>
        ) : null}
      </Mui.Box>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{
              startAdornment: (
                <Mui.InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: designColors.slate400 }} />
                </Mui.InputAdornment>
              ),
            }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>Row</Mui.TableCell>
                {IMPORT_FIELDS.map((field) => (
                  <Mui.TableCell key={field.key} sx={{ ...tableHeadSx, ...(MONEY_FIELDS.has(field.key) ? { textAlign: "right" } : {}) }}>
                    {field.label}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageRows.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={IMPORT_FIELDS.length + 1} sx={{ ...cellSx, textAlign: "center", py: 4 }}>
                    No rows to show.
                  </Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageRows.map((result) => (
                  <Mui.TableRow
                    key={result.rowNumber}
                    {...(result.row
                      ? {}
                      : {
                          sx: errorRowSx,
                          tabIndex: 0,
                          title: "Click to correct this row",
                          onClick: () => onFixRow(result.rowNumber),
                          onKeyDown: (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onFixRow(result.rowNumber);
                            }
                          },
                        })}
                  >
                    <Mui.TableCell sx={cellSx}>{result.rowNumber}</Mui.TableCell>
                    {IMPORT_FIELDS.map((field) => (
                      <Mui.TableCell key={field.key} sx={{ ...cellSx, whiteSpace: "nowrap", ...(MONEY_FIELDS.has(field.key) ? { textAlign: "right" } : {}) }}>
                        {previewValue(result, field.key)}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Edit an error row (opened from the preview) ----

const textOf = (value) => (isBlankText(value) ? "" : String(value).trim());

// Panel form values from the row's file values. Values the pickers can't
// show (an unknown type or mode, a date that can't be read) start blank so
// the user picks a valid one; the error banner still names the original
// value. Payroll Month is a text box, so it keeps the file's text.
const rowToFixForm = (raw) => ({
  employee_id: textOf(raw.employee_id),
  type: isBlankText(raw.type) ? "" : matchType(raw.type) || "",
  amount: textOf(raw.amount),
  description: textOf(raw.description),
  reason: textOf(raw.reason),
  request_date: isBlankText(raw.request_date) ? "" : parseDate(raw.request_date) || "",
  payroll_month: textOf(raw.payroll_month),
  repayment_mode: isBlankText(raw.repayment_mode) ? "" : matchMode(raw.repayment_mode) || "",
  repayment_date: isBlankText(raw.repayment_date) ? "" : parseDate(raw.repayment_date) || "",
  repayment_frequency: isBlankText(raw.repayment_frequency) ? "" : matchFrequency(raw.repayment_frequency) || "",
  no_of_installments: textOf(raw.no_of_installments),
});

const fieldLabel = (key) => {
  const field = IMPORT_FIELDS.find((item) => item.key === key);
  return `${field.label}${field.required ? " *" : ""}`;
};

const TYPE_OPTIONS = toOptions(TYPES);
const MODE_OPTIONS = toOptions(REPAYMENT_MODES);
const FREQUENCY_OPTIONS = [{ value: "", label: "Monthly (default)" }, ...toOptions(FREQUENCIES)];

// Side panel over the import: the row's errors in a red box on top, its
// values below to correct, then Re-Verify runs the same browser check and
// the server's validate API for this row. A row that passes turns valid and
// the panel closes; otherwise the new errors show.
const RowFixPanel = ({ row, onClose, onVerify }) => {
  const [form, setForm] = useState(() => rowToFixForm(row.raw));
  const [errors, setErrors] = useState(row.errors);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const verify = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await onVerify(row.rowNumber, form);
      if (next?.row) onClose();
      else if (next) setErrors(next.errors);
    } catch (error) {
      setErrors([`Could not check this row: ${error.message || "please try again."}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={busy ? undefined : onClose}
      hideExpand
      title="Edit Loan / Advance Row"
      subtitle={`Row ${row.rowNumber}. Correct the values and re-verify.`}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Verifying..." : "Re-Verify"}
          primaryProps={{ disabled: busy, loading: busy, onClick: verify }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {errors.length ? (
          <RequestInfoBanner
            tone="danger"
            title={errors.length > 1 ? `${errors.length} errors in this row` : "Error in this row"}
            body={errors.map((message) => (
              <Mui.Box key={message} component="span" sx={{ display: "block" }}>• {message}</Mui.Box>
            ))}
          />
        ) : null}
        <FormSection title="Loan / Advance" info="Values from the file. Each row is created as a Pending salary request.">
          <Field label={fieldLabel("employee_id")} helper="Employee code, as in the employee records.">
            <TextField value={form.employee_id} onChange={set("employee_id")} placeholder="e.g. EMP001" />
          </Field>
          <Field label={fieldLabel("type")} helper="Advance or Loan. Blank = Advance.">
            <SelectField value={form.type} onChange={set("type")} options={TYPE_OPTIONS} placeholder="Advance" />
          </Field>
          <Field label={fieldLabel("amount")} helper="Amount requested, more than 0.">
            <TextField value={form.amount} onChange={set("amount")} placeholder="e.g. 20000" />
          </Field>
          <Field label={fieldLabel("request_date")} helper="Day the request was made. Blank = today.">
            <DateField value={form.request_date} onChange={set("request_date")} />
          </Field>
          <Field label={fieldLabel("description")} helper="Short reference for the request, up to 200 characters. Optional." wide>
            <TextField value={form.description} onChange={set("description")} placeholder="e.g. Medical loan" />
          </Field>
          <Field label={fieldLabel("reason")} helper="Why the employee needs the money." wide>
            <TextField value={form.reason} onChange={set("reason")} placeholder="e.g. Medical emergency" />
          </Field>
          <Field label={fieldLabel("payroll_month")} helper="Advance only: the payroll it is recovered in full. Blank = this month." wide>
            <TextField value={form.payroll_month} onChange={set("payroll_month")} placeholder="e.g. Oct 2026" />
          </Field>
          <Field label={fieldLabel("repayment_mode")} helper="Loan only: full or in instalments.">
            <SelectField value={form.repayment_mode} onChange={set("repayment_mode")} options={MODE_OPTIONS} placeholder="Full or Installment" />
          </Field>
          <Field label={fieldLabel("repayment_date")} helper="Loan only: when repayment starts.">
            <DateField value={form.repayment_date} onChange={set("repayment_date")} />
          </Field>
          <Field label={fieldLabel("repayment_frequency")} helper="Loan only: how often it is repaid.">
            <SelectField value={form.repayment_frequency} onChange={set("repayment_frequency")} options={FREQUENCY_OPTIONS} placeholder="Monthly (default)" />
          </Field>
          <Field label={fieldLabel("no_of_installments")} helper="Installment mode only: 2 or more.">
            <TextField value={form.no_of_installments} onChange={set("no_of_installments")} placeholder="e.g. 4" />
          </Field>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Result ----

// The import API's answer: counts plus one row per record sent.
const RESULT_TABS = [
  { key: "all", label: "All Rows" },
  { key: "success", label: "Imported" },
  { key: "failed", label: "Failed" },
];

const ResultStep = ({ result }) => {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  const counts = { all: rows.length, success: rows.filter((row) => row.status === "success").length, failed: rows.filter((row) => row.status !== "success").length };
  const success = Number(result?.success ?? counts.success);
  const failed = Number(result?.failed ?? counts.failed);
  // Opens on the failed rows when there are any (same as the other imports).
  const [tab, setTab] = useState(failed ? "failed" : "all");
  const shown = tab === "all" ? rows : rows.filter((row) => (tab === "success" ? row.status === "success" : row.status !== "success"));
  const { pageItems, footer } = usePaged(shown, tab);
  return (
    <Mui.Stack spacing={1.5}>
      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>{failed ? "Import Finished With Errors" : "Import Complete"}</Mui.Typography>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Records" value={Number(result?.total ?? rows.length).toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Imported" value={success.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("success")} isActive={tab === "success"} activeBorder />
          <StatCard icon={CancelOutlined} title="Failed" value={failed.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("failed")} isActive={tab === "failed"} activeBorder />
        </Mui.Box>
      </Mui.Box>
      <RequestInfoBanner
        title="Requests created"
        body="Each imported row is a Pending request sent to the employee's approver. It follows the normal approve / reject flow."
      />
      {rows.length ? (
        <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
          <Mui.Box sx={{ p: 2 }}>
            <TabButtons tabs={RESULT_TABS} tab={tab} counts={counts} onChange={setTab} />
          </Mui.Box>
          <Mui.TableContainer sx={{ overflowX: "auto" }}>
            <Mui.Table size="small">
              <Mui.TableHead>
                <Mui.TableRow>
                  {["Employee ID", "Employee", "Type", "Amount", "Status", "Message"].map((label) => (
                    <Mui.TableCell key={label} sx={{ ...tableHeadSx, ...(label === "Amount" ? { textAlign: "right" } : {}) }}>{label}</Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {pageItems.length === 0 ? (
                  <Mui.TableRow>
                    <Mui.TableCell colSpan={6} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                  </Mui.TableRow>
                ) : pageItems.map((row, index) => {
                  const ok = row.status === "success";
                  return (
                    <Mui.TableRow key={`${row.index ?? index}-${row.employee_id}`}>
                      <Mui.TableCell sx={cellSx}>{rawText(row.employee_id)}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{rawText(row.employee_name)}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{rawText(row.type)}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, textAlign: "right" }}>{row.amount != null ? formatMoney(row.amount) : "–"}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold, color: ok ? designColors.tealDark : errorText }}>{ok ? "Imported" : "Failed"}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", color: ok ? undefined : errorText }}>{rawText(row.message)}</Mui.TableCell>
                    </Mui.TableRow>
                  );
                })}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          {footer}
        </Mui.Box>
      ) : null}
    </Mui.Stack>
  );
};

// ---- Wizard ----

// applyServerChecks adds preview-only keys; they are not part of the record.
const toRecord = ({ payable_amount: _amount, pay_schedule_name: _cycle, ...record }) => record;

export const LoanImport = ({ open, onClose, onImported, domain, companyId }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  // Every file row after the browser check and the server's validate API
  // ({ rowNumber, raw, errors, row }). Rows corrected in the Edit panel are
  // replaced here one at a time.
  const [results, setResults] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [result, setResult] = useState(null);
  const validateRef = useRef(null);
  // Row number of the error row open in the Edit panel.
  const [fixRowNumber, setFixRowNumber] = useState(null);
  const fixRow = fixRowNumber == null ? null : results.find((item) => item.rowNumber === fixRowNumber) || null;

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setFile(null);
    setFileError("");
    setMapping({});
    setResults([]);
    setValidateError("");
    setFixRowNumber(null);
    setSaving(false);
    setSaveError("");
    setResult(null);
    return () => validateRef.current?.abort();
  }, [open]);

  const records = results.filter((item) => item.row).map((item) => toRecord(item.row));

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > IMPORT_MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const { headers, rows } = splitSheetRows(await readSheet(picked));
      if (!headers.length || !rows.length) {
        setFile(null);
        setFileError("The file has no data rows under the header row.");
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows, rowCount: rows.length });
      setMapping(autoMapColumns(headers, rows));
    } catch (error) {
      setFile(null);
      setFileError(error.message);
    }
  };

  // Sends the rows that passed the browser check to the validate API.
  // Returns the checked rows with the server's answer applied, or null when
  // the server could not be reached.
  const validateRows = async (rowsChecked) => {
    validateRef.current?.abort();
    const toCheck = rowsChecked.filter((item) => item.row).map((item) => item.row);
    setValidateError("");
    if (!toCheck.length) return rowsChecked;
    const controller = new AbortController();
    validateRef.current = controller;
    setValidating(true);
    try {
      const data = await validateAdvanceImport(domain, toCheck, companyId, controller.signal);
      if (controller.signal.aborted) return null;
      return applyServerChecks(rowsChecked, data.rows, "row");
    } catch (error) {
      if (!controller.signal.aborted) setValidateError(error.message);
      return null;
    } finally {
      if (!controller.signal.aborted) setValidating(false);
    }
  };

  // Map Columns > Next: browser check, then the server check, then preview.
  const handleMapNext = async () => {
    const checkedRows = await validateRows(validateImportRows(file.rows, mapping));
    if (!checkedRows) return;
    setResults(checkedRows);
    setStep(2);
  };

  // Edit panel > Re-Verify: the corrected values go through the same browser
  // check, then (if that passes) the validate API for this one row. The row
  // is replaced in the preview either way, so the table shows the new values.
  // Throws when the server can't be reached (the panel shows it).
  const verifyRow = async (rowNumber, raw) => {
    let next = checkImportRow(rowNumber, raw);
    if (next.row) {
      const data = await validateAdvanceImport(domain, [next.row], companyId);
      next = applyServerChecks([next], data.rows, "row")[0];
    }
    setResults((current) => current.map((item) => (item.rowNumber === rowNumber ? next : item)));
    return next;
  };

  const handleImport = async () => {
    if (saving || !records.length) return;
    setSaving(true);
    setSaveError("");
    try {
      const answer = await importAdvanceRequests(domain, records, companyId);
      setResult(answer);
      setStep(3);
      onImported?.(answer);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingRequiredFields(mapping).length === 0);

  const footer =
    step === 3 ? (
      <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
        <PrimaryButton onClick={onClose}>Done</PrimaryButton>
      </Mui.Stack>
    ) : (
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
        <SecondaryButton disabled={saving || validating} onClick={onClose}>Cancel</SecondaryButton>
        <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
          {step > 0 ? (
            <SecondaryButton disabled={saving || validating} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>
              Back
            </SecondaryButton>
          ) : null}
          {step < 2 ? (
            <PrimaryButton
              endIcon={<ArrowForward />}
              disabled={!canContinue || validating}
              loading={step === 1 && validating}
              onClick={() => (step === 1 ? handleMapNext() : setStep(step + 1))}
            >
              {step === 1 && validating ? "Validating..." : "Next"}
            </PrimaryButton>
          ) : (
            <PrimaryButton startIcon={<FileUploadOutlined />} disabled={records.length === 0 || saving} loading={saving} onClick={handleImport}>
              {`Import ${records.length ? records.length.toLocaleString("en-IN") : ""}`}
            </PrimaryButton>
          )}
        </Mui.Stack>
      </Mui.Stack>
    );

  const subtitles = [
    "Upload a file of loan and advance requests.",
    "Map your file columns to the request fields.",
    "Review your data and create the requests.",
    "Loans & Advances import finished.",
  ];

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={open}
      onToggleExpand={() => {}}
      hideExpand
      title="Import Loans & Advances"
      subtitle={subtitles[step]}
      // Payroll -> Payroll home; Loans & Advances -> back to the list (closes
      // this panel). Not while an import is being saved. Same as the other
      // Pay Inputs imports.
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!saving) history.push("/admin/payroll/setup"); } },
        "Pay Inputs",
        { label: "Loans & Advances", onClick: () => { if (!saving) onClose(); } },
        "Import",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 2, sm: 3 } }}
      footer={footer}
    >
      <Mui.Stack spacing={2}>
        <Mui.Box sx={{ ...detailCardSx, py: 1.5 }}>
          <WizardStepper steps={STEPS} step={Math.min(step, STEPS.length - 1)} />
        </Mui.Box>
        {step === 0 ? (
          <UploadStep file={file} error={fileError} onFile={handleFile} />
        ) : step === 1 ? (
          <MapStep
            file={file}
            mapping={mapping}
            onMappingChange={(index, fieldKey) =>
              setMapping((current) => {
                const next = { ...current };
                if (fieldKey) next[index] = fieldKey;
                else delete next[index];
                return next;
              })
            }
            onReset={() => setMapping(autoMapColumns(file.headers, file.rows))}
            onChangeFile={() => setStep(0)}
          />
        ) : step === 2 ? (
          <PreviewStep results={results} onFixRow={setFixRowNumber} />
        ) : (
          <ResultStep result={result} />
        )}
        {/* Same as the other imports: errors sit at the bottom, next to the
            Next / Import buttons. */}
        {validateError && step === 1 ? <RequestInfoBanner tone="danger" title="Could not validate the rows" body={validateError} /> : null}
        {saveError && step === 2 ? <RequestInfoBanner tone="danger" title="Could not import loans & advances" body={saveError} /> : null}
      </Mui.Stack>
      {/* key: a fresh form for each row opened. */}
      {fixRow && step === 2 && !result ? (
        <RowFixPanel key={fixRow.rowNumber} row={fixRow} onClose={() => setFixRowNumber(null)} onVerify={verifyRow} />
      ) : null}
    </GlobalSidePanel>
  );
};
